import { useQuery } from "@apollo/client";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import Spinner from "components/base/Spinner";
import { useState } from "react";
import { FETCH_PETE_FCST } from "../graphql";

function normalizeMonth(rawMonth) {
  if (!rawMonth) return null;
  const value = String(rawMonth).trim();
  if (/^\d{4}-\d{2}$/.test(value)) return value;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value.slice(0, 7);
  return null;
}

function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function normalizeProductId(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  if (/^\d+$/.test(raw)) return String(parseInt(raw, 10));
  return raw;
}

function normalizeSkuKey(value) {
  return String(value || "")
    .trim()
    .toUpperCase();
}

const IncSheetDataView = ({ category, confirmHandler, saveCategory }) => {
  const [saving, setSaving] = useState(false);

  const { loading, error, data } = useQuery(FETCH_PETE_FCST, {
    variables: {
      userId: category.incFcstSheetUserId,
      filePath: category.incFcstFilePath,
      sheetName: category.incFcstSheetName,
      returnRows: true,
    },
    fetchPolicy: "network-only",
  });

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const products = data.salesGetProductsForFcst;

  let peteFcst = {};
  let sheetRows = [];
  try {
    const parsed = JSON.parse(data?.salesPeteForecast || "{}");
    peteFcst = parsed && parsed.data;
    sheetRows = parsed && parsed.rows;
  } catch {
    window.alert("Failed to parse Pete forecast data.");
    return null;
  }

  const productMapBySku = {};
  (products || []).forEach((product) => {
    const sku = String(product.number || "").trim();
    if (!sku) return;
    productMapBySku[normalizeSkuKey(sku)] = normalizeProductId(product.id);
  });

  const rawRows = Array.isArray(sheetRows)
    ? sheetRows.map((row) => ({
        sku: String(row?.sku || "").trim(),
        fcst:
          row?.forecast && typeof row.forecast === "object" ? row.forecast : {},
      }))
    : Object.entries(peteFcst || {}).map(([sku, monthData]) => ({
        sku: String(sku || "").trim(),
        fcst: monthData && typeof monthData === "object" ? monthData : {},
      }));

  // Keep sheet order and dedupe by SKU (first occurrence wins).
  const dedupedRows = [];
  const seenSkuSet = new Set();
  rawRows.forEach((row) => {
    const skuKey = normalizeSkuKey(row?.sku);
    if (!skuKey || seenSkuSet.has(skuKey)) return;
    seenSkuSet.add(skuKey);
    dedupedRows.push(row);
  });

  const rows = dedupedRows.map((row) => {
    const normalizedFcst = {};
    Object.entries(row.fcst || {}).forEach(([rawMonth, qty]) => {
      const month = normalizeMonth(rawMonth);
      if (!month) return;
      normalizedFcst[month] = toNumber(qty);
    });
    return { sku: row.sku, fcst: normalizedFcst };
  });

  const monthSet = new Set();
  const unmappedSkus = [];
  const mappedSkus = [];

  rows.forEach((row) => {
    Object.keys(row.fcst).forEach((month) => monthSet.add(month));
    if (productMapBySku[normalizeSkuKey(row.sku)]) mappedSkus.push(row.sku);
    else if (row.sku) unmappedSkus.push(row.sku);
  });

  const months = [...monthSet].sort((a, b) => a.localeCompare(b));

  const importedFcstData = {};
  (products || []).forEach((product) => {
    const productId = normalizeProductId(product.id);
    importedFcstData[productId] = months.reduce((acc, month) => {
      acc[month] = 0;
      return acc;
    }, {});
  });

  rows.forEach((row) => {
    const productId = productMapBySku[normalizeSkuKey(row.sku)];
    if (!productId) return;
    months.forEach((month) => {
      importedFcstData[productId][month] = toNumber(row.fcst[month] ?? 0);
    });
  });

  const preview = {
    rows,
    months,
    mappedCount: mappedSkus.length,
    unmappedCount: unmappedSkus.length,
    preparedImportData: {
      importedMonths: months,
      importedFcstData,
      mappedCount: mappedSkus.length,
      unmappedSkus,
      mappedProductIds: [
        ...new Set(
          mappedSkus.map((sku) => productMapBySku[normalizeSkuKey(sku)]),
        ),
      ],
    },
  };

  const handleConfirmImport = async () => {
    if (typeof saveCategory !== "function") {
      window.alert("saveCategory is required before confirm import.");
      return;
    }

    const existingProductIds = (category.products || []).map((product) =>
      normalizeProductId(product.id),
    );
    const nextProductIds = [
      ...new Set([
        ...existingProductIds,
        ...(preview.preparedImportData.mappedProductIds || []),
      ]),
    ];

    setSaving(true);
    try {
      await saveCategory({
        variables: {
          id: category.id,
          productIds: nextProductIds,
        },
      });
      await confirmHandler(preview.preparedImportData);
    } catch {
      return;
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="text-sm text-orange-600">
        <p>
          Forecast data read from {category.incFcstFilePath}(
          {category.incFcstSheetName})
        </p>
        <p>
          {preview.rows.length} SKU(s) found in sheet, {preview.mappedCount} can
          be imported to current forecast category
          {preview.unmappedCount > 0
            ? `, ${preview.unmappedCount} not matched`
            : ""}
          .
        </p>
        <p>
          If the same SKU appears multiple times in the sheet, the system will
          use the first record.
        </p>
      </div>

      <div className="overflow-auto border max-h-[50vh] mt-4 whitespace-nowrap font-code">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-700 text-left sticky top-0">
              <th className="px-3 py-2">#</th>
              <th className="px-3 py-2">SKU</th>
              {preview.months.map((month) => (
                <th key={month} className="px-3 py-2 text-right">
                  {month}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {preview.rows.map((row, index) => {
              const notMatched = !productMapBySku[normalizeSkuKey(row.sku)];
              return (
                <tr
                  key={row.sku || `row-${index}`}
                  className={`border-t ${notMatched ? "bg-red-50 text-red-700" : ""}`}
                >
                  <td className="px-3 py-2 whitespace-nowrap">{index + 1}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {row.sku || "-"}
                    {notMatched ? " (sku not match)" : ""}
                  </td>
                  {preview.months.map((month) => (
                    <td
                      key={`${row.sku}-${month}`}
                      className="px-3 py-2 text-right"
                    >
                      {row.fcst[month] ?? "-"}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-6 space-y-4">
        {preview.unmappedCount > 0 && (
          <div className="text-sm text-orange-600 font-semibold">
            <p>
              ⚠️ WIS will skip those the unmatched SKU(s) in the sheet. If you
              have any questions, please contact Waboba support team for help.
            </p>
            <p>
              Unmatched SKU(s):{" "}
              {preview.preparedImportData.unmappedSkus.join(", ")}
            </p>
          </div>
        )}

        <Button
          primary
          onClick={handleConfirmImport}
          loading={saving}
          disabled={preview.rows.length === 0 || preview.mappedCount === 0}
        >
          Confirm Import
        </Button>
      </div>
    </div>
  );
};

export default IncSheetDataView;
